/*
 * Nuvoro theme – global runtime.
 * Loaded (deferred) on every page from layout/theme.liquid.
 *
 * Provides shared utilities (fetchConfig, debounce, throttle, focus trapping),
 * a tiny pub/sub bus, generic custom elements and the scroll-reveal animation observer.
 * Every custom element definition is guarded so section scripts may safely extend or
 * re-declare them. Everything public is attached to `window` (configurable properties), so other
 * scripts can use the plain names (e.g. `debounce`, `PUB_SUB_EVENTS`, `class X extends ModalDialog`)
 * without risking redeclaration errors.
 */

(() => {
  'use strict';

  /* -------------------------------------------------------------------------- */
  /* Utilities                                                                  */
  /* -------------------------------------------------------------------------- */

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  function debounce(fn, wait = 300) {
    let timer = null;
    return function debounced(...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function throttle(fn, wait = 100) {
    let last = 0;
    let trailing = null;
    return function throttled(...args) {
      const now = Date.now();
      const remaining = wait - (now - last);
      if (remaining <= 0) {
        window.clearTimeout(trailing);
        trailing = null;
        last = now;
        fn.apply(this, args);
      } else if (!trailing) {
        trailing = window.setTimeout(() => {
          last = Date.now();
          trailing = null;
          fn.apply(this, args);
        }, remaining);
      }
    };
  }

  const FOCUSABLE_SELECTOR = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe',
  ].join(',');

  function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement || getComputedStyle(el).position === 'fixed'
    );
  }

  const focusTrap = { container: null, onKeydown: null };

  function removeTrapFocus(elementToFocus = null) {
    if (focusTrap.onKeydown) document.removeEventListener('keydown', focusTrap.onKeydown, true);
    focusTrap.container = null;
    focusTrap.onKeydown = null;
    if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
  }

  function trapFocus(container, elementToFocus = container) {
    if (!container) return;
    removeTrapFocus();
    focusTrap.container = container;
    focusTrap.onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = getFocusableElements(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !container.contains(active);
      if (event.shiftKey && (active === first || active === container || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', focusTrap.onKeydown, true);

    const target = elementToFocus || container;
    if (target && typeof target.focus === 'function') {
      if (!target.hasAttribute('tabindex') && !target.matches(FOCUSABLE_SELECTOR)) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      const selectable = ['search', 'text', 'email', 'url'];
      if (target.tagName === 'INPUT' && selectable.includes(target.type) && target.value) {
        target.setSelectionRange(0, target.value.length);
      }
    }
  }

  function onKeyUpEscape(event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    const openDetails = event.target.closest('details[open]');
    if (!openDetails) return;
    const summary = openDetails.querySelector('summary');
    openDetails.removeAttribute('open');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    }
  }

  /** Stops every playing media element on the page (videos, embeds, 3D models). */
  function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach((frame) => {
      if (frame.contentWindow) frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach((frame) => {
      if (frame.contentWindow) frame.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('deferred-media video, media-gallery template video').forEach((video) => video.pause());
    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  }

  /** Formats an amount in cents with a Shopify money format string ({{amount}}, {{amount_with_comma_separator}}, ...). */
  function formatMoney(cents, format) {
    const moneyFormat =
      format || (window.theme && window.theme.moneyFormat) || (window.Shopify && window.Shopify.money_format) || '{{amount}}';
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const group = (amount, precision, thousands = ',', decimal = '.') => {
      if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '0';
      const fixed = (Number(amount) / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const withSeparators = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${withSeparators}${decimal}${fraction}` : withSeparators;
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return group(value, 0);
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        default:
          return group(value, 2);
      }
    });
  }

  /* -------------------------------------------------------------------------- */
  /* Pub/Sub                                                                    */
  /* -------------------------------------------------------------------------- */

  const PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    optionValueSelectionChange: 'option-value-selection-change',
    variantChange: 'variant-change',
    cartError: 'cart-error',
  };

  const pubSubListeners = new Map();

  function subscribe(eventName, callback) {
    if (!pubSubListeners.has(eventName)) pubSubListeners.set(eventName, new Set());
    pubSubListeners.get(eventName).add(callback);
    return function unsubscribe() {
      const set = pubSubListeners.get(eventName);
      if (set) set.delete(callback);
    };
  }

  function publish(eventName, data) {
    const set = pubSubListeners.get(eventName);
    if (!set) return Promise.resolve();
    const results = Array.from(set).map((callback) => {
      try {
        return callback(data);
      } catch (error) {
        console.error(error);
        return undefined;
      }
    });
    return Promise.all(results);
  }



  if (window.Shopify === undefined) window.Shopify = {};
  if (!window.Shopify.routes) window.Shopify.routes = { root: (window.routes && window.routes.root_url) || '/' };

  const isRtlDocument = () => document.documentElement.getAttribute('dir') === 'rtl' || document.body.dataset.rtl === 'true';
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const defineElement = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* -------------------------------------------------------------------------- */
  /* Modal dialog + opener                                                      */
  /* -------------------------------------------------------------------------- */

  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.handleKeyUp = (event) => {
        if (event.key === 'Escape') this.hide();
      };
      this.addEventListener('keyup', this.handleKeyUp);
      this.addEventListener('click', (event) => {
        const closer = event.target.closest('[id^="ModalClose-"], [data-modal-close]');
        if (closer && this.contains(closer)) {
          this.hide();
          return;
        }
        if (this.classList.contains('media-modal')) return;
        if (event.target === this) this.hide();
      });
      if (this.classList.contains('media-modal')) {
        this.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model, button, a')) this.hide();
        });
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      const lazy = this.querySelector('.template-popup');
      if (lazy && typeof lazy.loadContent === 'function') lazy.loadContent();
      trapFocus(this, this.querySelector('[role="dialog"]') || this);
      pauseAllMedia();
      this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      pauseAllMedia();
      this.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
    }
  }
  defineElement('modal-dialog', ModalDialog);

  class ModalOpener extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const trigger = this.querySelector('button:not(.internal-video__play):not(.internal-video__sound-btn), [data-modal-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', (event) => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (!modal || typeof modal.show !== 'function') return;
        event.preventDefault();
        modal.show(trigger);
      });
    }
  }
  defineElement('modal-opener', ModalOpener);

  /* -------------------------------------------------------------------------- */
  /* <details> based components                                                 */
  /* -------------------------------------------------------------------------- */

  class DetailsModal extends HTMLElement {
    constructor() {
      super();
      this.detailsContainer = this.querySelector('details');
      this.summaryToggle = this.querySelector('summary');
      if (!this.detailsContainer || !this.summaryToggle) return;
      this.onBodyClick = this.onBodyClick.bind(this);
      this.detailsContainer.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.close();
      });
      this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
      this.summaryToggle.setAttribute('role', 'button');
      this.summaryToggle.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');
      const closeButton = this.querySelector('button[type="button"]');
      if (closeButton) closeButton.addEventListener('click', () => this.close());
    }

    isOpen() {
      return this.detailsContainer.hasAttribute('open');
    }

    onSummaryClick(event) {
      event.preventDefault();
      if (this.isOpen()) this.close();
      else this.open(event);
    }

    onBodyClick(event) {
      if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
    }

    open() {
      this.detailsContainer.setAttribute('open', '');
      this.summaryToggle.setAttribute('aria-expanded', 'true');
      document.body.addEventListener('click', this.onBodyClick);
      document.body.classList.add('overflow-hidden');
      const focusTarget = this.detailsContainer.querySelector('input:not([type="hidden"])');
      trapFocus(this.detailsContainer.querySelector('[tabindex="-1"]') || this.detailsContainer, focusTarget);
    }

    close(focusToggle = true) {
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
      this.detailsContainer.removeAttribute('open');
      this.summaryToggle.setAttribute('aria-expanded', 'false');
      document.body.removeEventListener('click', this.onBodyClick);
      document.body.classList.remove('overflow-hidden');
    }
  }
  defineElement('details-modal', DetailsModal);

  class DetailsDisclosure extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');
      if (!this.mainDetailsToggle) return;
      this.summary = this.mainDetailsToggle.querySelector('summary');
      this.content = this.summary ? this.summary.nextElementSibling : null;
      this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
      this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onToggle() {
      const open = this.mainDetailsToggle.hasAttribute('open');
      if (this.summary) this.summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!this.content || typeof this.content.getAnimations !== 'function') return;
      if (!this.animations) this.animations = this.content.getAnimations();
      this.animations.forEach((animation) => (open ? animation.play() : animation.cancel()));
    }

    close() {
      this.mainDetailsToggle.removeAttribute('open');
      if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
    }
  }
  defineElement('details-disclosure', DetailsDisclosure);

  /* -------------------------------------------------------------------------- */
  /* Quantity input                                                             */
  /* -------------------------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      if (!this.input) return;
      this.input.addEventListener('change', () => this.validateQtyRules());
      this.querySelectorAll('.quantity__button, button[name="plus"], button[name="minus"]').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    connectedCallback() {
      if (!this.input) return;
      this.validateQtyRules();
      this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.validateQtyRules());
    }

    disconnectedCallback() {
      if (this.unsubscribeQuantity) this.unsubscribeQuantity();
    }

    readNumber(attribute, fallback) {
      const raw = this.input.getAttribute(attribute);
      const parsed = parseFloat(raw);
      return raw === null || raw === '' || Number.isNaN(parsed) ? fallback : parsed;
    }

    onButtonClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const previous = this.input.value;
      const step = this.readNumber('step', 1) || 1;
      const min = this.readNumber('min', 0);
      const max = this.readNumber('max', Infinity);
      const current = parseFloat(this.input.value) || 0;
      let next = button.name === 'plus' ? current + step : current - step;
      if (button.name !== 'plus' && current > min && next < min) next = min;
      next = Math.min(Math.max(next, min), max);
      this.input.value = String(next);
      if (previous !== this.input.value) this.input.dispatchEvent(this.changeEvent);
      else this.validateQtyRules();
    }

    validateQtyRules() {
      const value = parseFloat(this.input.value) || 0;
      const minus = this.querySelector('.quantity__button[name="minus"], button[name="minus"]');
      const plus = this.querySelector('.quantity__button[name="plus"], button[name="plus"]');
      if (minus) minus.classList.toggle('disabled', value <= this.readNumber('min', 0));
      if (plus) plus.classList.toggle('disabled', value >= this.readNumber('max', Infinity));
      const gifts = this.dataset.section ? document.getElementById(`quantity-gifts-${this.dataset.section}`) : null;
      if (gifts && typeof gifts.unlockGifts === 'function') gifts.unlockGifts(value);
    }
  }
  defineElement('quantity-input', QuantityInput);

  /* -------------------------------------------------------------------------- */
  /* Deferred media (video / 3D / iframe loaded on demand)                      */
  /* -------------------------------------------------------------------------- */

  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const poster = this.querySelector('[id^="Deferred-Poster-"], .deferred-media__poster');
      if (poster) poster.addEventListener('click', () => this.loadContent());
      if (this.dataset.autoload === 'true') this.loadContent(false);
    }

    loadContent(focus = true) {
      pauseAllMedia();
      if (this.getAttribute('loaded')) return;
      const template = this.querySelector('template');
      if (!template || !template.content.firstElementChild) return;
      const holder = document.createElement('div');
      holder.appendChild(template.content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', 'true');
      const media = holder.querySelector('video, model-viewer, iframe') || holder.firstElementChild;
      const appended = this.appendChild(media);
      if (appended.tagName === 'VIDEO' && appended.hasAttribute('autoplay')) {
        const attempt = appended.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }
      if (focus && typeof appended.focus === 'function') appended.focus();
    }
  }
  defineElement('deferred-media', DeferredMedia);

  /* -------------------------------------------------------------------------- */
  /* Internal (self-hosted) video                                               */
  /* -------------------------------------------------------------------------- */

  class InternalVideo extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.playButton = this.querySelector('.internal-video__play');
      this.soundButton = this.querySelector('.internal-video__sound-btn');
      this.timeline = this.querySelector('.internal-video__timeline');
      this.hidePlayButton = this.dataset.noPlayBtn === 'true';
      this.sourcesReady = !this.video.querySelector('source[data-src]');
      this.dragging = false;

      this.video.querySelectorAll('source[type="application/x-mpegURL"]').forEach((source) => source.remove());

      if (this.playButton) this.playButton.addEventListener('click', () => this.togglePlay());
      if (this.soundButton) this.soundButton.addEventListener('click', () => this.toggleSound());
      this.video.addEventListener('ended', () => this.setPlaying(false));
      this.video.addEventListener('pause', () => {
        if (!this.video.ended && this.dataset.autoplay !== 'true') this.setPlaying(false);
      });
      this.video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
      ['canplay', 'playing', 'play'].forEach((name) =>
        this.video.addEventListener(name, () => this.classList.remove('internal-video--loading'))
      );

      if (this.timeline) {
        this.video.addEventListener('timeupdate', () => this.updateTimeline());
        this.onPointerMove = (event) => {
          if (this.dragging) this.seek(event);
        };
        this.onPointerUp = () => {
          this.dragging = false;
        };
        this.timeline.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          this.dragging = true;
          this.seek(event);
        });
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
      }

      if (this.dataset.autoplay === 'true') {
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.observer.disconnect();
              this.startAutoplay();
            },
            { threshold: 0.05 }
          );
          this.observer.observe(this);
        } else {
          this.startAutoplay();
        }
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
      if (this.onPointerUp) window.removeEventListener('pointerup', this.onPointerUp);
    }

    loadSources() {
      if (this.sourcesReady) return;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.setAttribute('src', source.getAttribute('data-src'));
        source.removeAttribute('data-src');
      });
      this.sourcesReady = true;
      this.video.load();
    }

    setPlaying(playing) {
      this.classList.toggle('internal-video--playing', playing);
      if (this.playButton && this.hidePlayButton) this.playButton.style.visibility = playing ? 'hidden' : 'visible';
      if (this.playButton) {
        const strings = window.accessibilityStrings || {};
        const label = playing ? strings.pauseVideo : strings.playVideo;
        if (label) this.playButton.setAttribute('aria-label', label);
      }
    }

    startAutoplay() {
      this.loadSources();
      this.video.muted = true;
      const attempt = this.video.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt.then(() => this.setPlaying(true)).catch(() => this.setPlaying(false));
      }
    }

    play() {
      this.loadSources();
      const attempt = this.video.play();
      this.setPlaying(true);
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.setPlaying(false));
    }

    pause() {
      this.video.pause();
      this.setPlaying(false);
    }

    mute() {
      this.video.muted = true;
      this.classList.add('internal-video--muted');
    }

    togglePlay() {
      if (this.video.paused) this.play();
      else this.pause();
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      this.classList.toggle('internal-video--muted', this.video.muted);
    }

    updateTimeline() {
      if (!this.video.duration) return;
      this.style.setProperty('--completed', `${(this.video.currentTime / this.video.duration) * 100}%`);
    }

    seek(event) {
      if (!this.video.duration) return;
      const rect = this.timeline.getBoundingClientRect();
      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      this.video.currentTime = ratio * this.video.duration;
    }

    /** Called by sliders when the slide containing this video is no longer visible. */
    applyInactiveAction() {
      const action = this.dataset.actionOnInactive;
      if (action === 'pause') this.pause();
      else if (action === 'mute') this.mute();
    }
  }
  defineElement('internal-video', InternalVideo);

  /* -------------------------------------------------------------------------- */
  /* Slider component (scroll-snap)                                             */
  /* -------------------------------------------------------------------------- */
  /*
   * Markup contract:
   *   <slider-component data-autoplay="true" data-speed="5" data-loop="true" data-pause-videos="true">
   *     <ul id="Slider-ID" class="slider slider--mobile grid ..."> <li id="Slide-ID-1" class="slider__slide grid__item"> ...
   *     <div class="slider-buttons">
   *       <button type="button" class="slider-button slider-button--prev" name="previous">…</button>
   *       <div class="slider-counter"><span class="slider-counter--current">1</span> / <span class="slider-counter--total">3</span></div>
   *         or dots: <button class="slider-counter__link slider-counter__link--dots"><span class="dot"></span></button>
   *       <button type="button" class="slider-button slider-button--next" name="next">…</button>
   *       optional: <button type="button" class="slider-autoplay-toggle">…</button>
   *     </div>
   *   </slider-component>
   * Events: dispatches "slideChanged" with { currentPage, currentElement }.
   */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.slider = this.querySelector('[id^="Slider-"]') || this.querySelector('.slider');
      if (!this.slider) return;
      this.initialized = true;
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.currentPageElement = this.querySelector('.slider-counter--current');
      this.pageTotalElement = this.querySelector('.slider-counter--total');
      this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
      this.autoplayToggle = this.querySelector('.slider-autoplay-toggle');
      this.loop = this.dataset.loop === 'true' || this.dataset.autoplay === 'true';
      this.currentPage = 1;
      this.totalPages = 1;
      this.itemOffset = 0;

      this.onScroll = throttle(() => this.update(), 60);
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevButton) this.prevButton.addEventListener('click', (event) => this.onButtonClick(event, -1));
      if (this.nextButton) this.nextButton.addEventListener('click', (event) => this.onButtonClick(event, 1));
      this.dots.forEach((dot, index) =>
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          this.goToPage(this.visibleDotIndex(index) + 1);
        })
      );

      this.resizeObserver = new ResizeObserver(() => this.initPages());
      this.resizeObserver.observe(this.slider);
      this.initPages();
      this.setupAutoplay();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      this.stopTimer();
    }

    get isVertical() {
      const mobile = window.innerWidth < 750;
      return (mobile ? this.dataset.mobileVertical : this.dataset.desktopVertical) === 'true';
    }

    get direction() {
      return isRtlDocument() && !this.isVertical ? -1 : 1;
    }

    get sliderItems() {
      const items = this.slider.querySelectorAll('[id^="Slide-"]');
      return Array.from(items.length ? items : this.slider.querySelectorAll('.slider__slide'));
    }

    resetPages() {
      this.initPages();
    }

    initPages() {
      this.visibleItems = this.sliderItems.filter((item) => item.clientWidth > 0);
      if (this.visibleItems.length < 2) {
        this.totalPages = this.visibleItems.length;
        this.itemOffset = 0;
        this.update();
        return;
      }
      const frame = this.slider.getBoundingClientRect();
      const first = this.visibleItems[0].getBoundingClientRect();
      const second = this.visibleItems[1].getBoundingClientRect();
      if (this.isVertical) {
        this.itemOffset = Math.abs(second.top - first.top);
        const gap = Math.max(this.itemOffset - first.height, 0);
        const leading = first.top - frame.top + this.slider.scrollTop;
        this.slidesPerPage = Math.max(1, Math.floor((this.slider.clientHeight - leading + gap + 1) / (this.itemOffset || 1)));
      } else {
        this.itemOffset = Math.abs(second.left - first.left);
        const gap = Math.max(this.itemOffset - first.width, 0);
        const leading =
          this.direction === 1
            ? first.left - frame.left + this.slider.scrollLeft
            : frame.right - first.right - this.slider.scrollLeft;
        this.slidesPerPage = Math.max(1, Math.floor((this.slider.clientWidth - leading + gap + 1) / (this.itemOffset || 1)));
      }
      this.totalPages = Math.max(1, this.visibleItems.length - this.slidesPerPage + 1);
      this.update();
    }

    scrollPosition() {
      return this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft * this.direction;
    }

    maxScroll() {
      return this.isVertical
        ? this.slider.scrollHeight - this.slider.clientHeight
        : this.slider.scrollWidth - this.slider.clientWidth;
    }

    update() {
      const previous = this.currentPage;
      if (this.itemOffset > 0) {
        const position = this.scrollPosition();
        this.currentPage = position >= this.maxScroll() - 2 ? this.totalPages : Math.round(position / this.itemOffset) + 1;
        this.currentPage = Math.min(Math.max(this.currentPage, 1), Math.max(this.totalPages, 1));
      } else {
        this.currentPage = 1;
      }

      if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
      if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages;

      let visibleIndex = 0;
      this.dots.forEach((dot) => {
        const hiddenDot = dot.classList.contains('hidden');
        const active = !hiddenDot && visibleIndex === this.currentPage - 1;
        dot.classList.toggle('slider-counter__link--active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        if (!hiddenDot) visibleIndex += 1;
      });

      if (!this.loop) {
        const atStart = this.scrollPosition() <= 1;
        const atEnd = this.scrollPosition() >= this.maxScroll() - 2;
        if (this.prevButton) this.prevButton.toggleAttribute('disabled', atStart);
        if (this.nextButton) this.nextButton.toggleAttribute('disabled', atEnd || this.totalPages <= 1);
      }

      if (previous !== this.currentPage) {
        this.handleInactiveVideos();
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: { currentPage: this.currentPage, currentElement: this.visibleItems[this.currentPage - 1] },
          })
        );
      }
    }

    visibleDotIndex(index) {
      return this.dots.slice(0, index).filter((dot) => !dot.classList.contains('hidden')).length;
    }

    scrollToOffset(offset, smooth = true) {
      const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';
      if (this.isVertical) this.slider.scrollTo({ top: offset, behavior });
      else this.slider.scrollTo({ left: offset * this.direction, behavior });
    }

    goToPage(page, smooth = true) {
      const target = Math.min(Math.max(page, 1), Math.max(this.totalPages, 1));
      this.scrollToOffset((target - 1) * this.itemOffset, smooth);
    }

    goToSlide(element, smooth = true) {
      const index = this.visibleItems ? this.visibleItems.indexOf(element) : -1;
      if (index >= 0) this.goToPage(index + 1, smooth);
    }

    next() {
      if (this.currentPage >= this.totalPages) {
        if (this.loop) this.goToPage(1);
        return;
      }
      this.goToPage(this.currentPage + 1);
    }

    prev() {
      if (this.currentPage <= 1) {
        if (this.loop) this.goToPage(this.totalPages);
        return;
      }
      this.goToPage(this.currentPage - 1);
    }

    onButtonClick(event, direction) {
      event.preventDefault();
      const step = parseInt(event.currentTarget.dataset.step || '1', 10) || 1;
      if (step === 1) {
        if (direction > 0) this.next();
        else this.prev();
      } else {
        this.goToPage(this.currentPage + direction * step);
      }
      this.restartTimer();
    }

    handleInactiveVideos() {
      if (this.dataset.pauseVideos === 'false') return;
      const active = this.visibleItems ? this.visibleItems[this.currentPage - 1] : null;
      this.querySelectorAll('internal-video[data-action-on-inactive]').forEach((video) => {
        const slide = video.closest('[id^="Slide-"], .slider__slide');
        if (slide && slide !== active && typeof video.applyInactiveAction === 'function') video.applyInactiveAction();
      });
    }

    /* --- autoplay --- */
    setupAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      this.speed = Math.max(parseFloat(this.dataset.speed || '5'), 1) * 1000;
      this.userPaused = false;
      this.inView = false;
      this.hovered = false;
      this.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.stopTimer();
      });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.restartTimer();
      });
      this.addEventListener('focusin', () => this.stopTimer());
      this.addEventListener('focusout', () => this.restartTimer());
      if (this.autoplayToggle) {
        this.autoplayToggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.updateToggleLabel();
          this.restartTimer();
        });
        this.updateToggleLabel();
      }
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.inView = entries.some((entry) => entry.isIntersecting);
        this.restartTimer();
      });
      this.visibilityObserver.observe(this);
    }

    updateToggleLabel() {
      if (!this.autoplayToggle) return;
      const strings = window.accessibilityStrings || {};
      this.autoplayToggle.classList.toggle('slider-autoplay-toggle--paused', this.userPaused);
      const label = this.userPaused ? strings.playSlideshow : strings.pauseSlideshow;
      if (label) this.autoplayToggle.setAttribute('aria-label', label);
    }

    play() {
      this.userPaused = false;
      this.updateToggleLabel();
      this.restartTimer();
    }

    pause() {
      this.userPaused = true;
      this.updateToggleLabel();
      this.stopTimer();
    }

    stopTimer() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    restartTimer() {
      this.stopTimer();
      if (this.dataset.autoplay !== 'true' || this.userPaused || this.hovered || !this.inView || prefersReducedMotion()) return;
      if (this.totalPages <= 1) return;
      this.timer = window.setInterval(() => this.next(), this.speed);
    }
  }
  defineElement('slider-component', SliderComponent);

  /* -------------------------------------------------------------------------- */
  /* Copy button (section id helper, discount codes, ...)                        */
  /* -------------------------------------------------------------------------- */

  class CopyButton extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      if (!this.hasAttribute('tabindex') && this.tagName === 'COPY-BUTTON') this.setAttribute('tabindex', '0');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      this.addEventListener('click', () => this.copy());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.copy();
        }
      });
    }

    get textToCopy() {
      return (this.dataset.content || '').trim();
    }

    async copy() {
      const text = this.textToCopy;
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied) {
        const helper = document.createElement('textarea');
        helper.value = text;
        helper.setAttribute('readonly', '');
        helper.className = 'visually-hidden';
        document.body.appendChild(helper);
        helper.select();
        try {
          document.execCommand('copy');
        } catch (error) {
          /* clipboard unavailable */
        }
        helper.remove();
      }
      this.onCopied();
    }

    onCopied() {
      this.dataset.success = 'true';
      window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => {
        this.dataset.success = 'false';
      }, 3000);
      this.dispatchEvent(new CustomEvent('copied', { bubbles: true, detail: { text: this.textToCopy } }));
    }
  }
  defineElement('copy-button', CopyButton);

  /* -------------------------------------------------------------------------- */
  /* Localization (country / language) disclosure                               */
  /* -------------------------------------------------------------------------- */

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.input = this.querySelector('input[name="locale_code"], input[name="country_code"]');
      this.button = this.querySelector('.disclosure__button, button[aria-expanded]') || this.querySelector('button');
      this.panel = this.querySelector('.disclosure__list-wrapper');
      if (!this.button || !this.panel) return;

      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.togglePanel();
      });
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        this.hidePanel();
        this.button.focus();
      });
      this.querySelectorAll('a[data-value]').forEach((link) =>
        link.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.input) this.input.value = link.dataset.value;
          const form = this.querySelector('form');
          if (form) form.submit();
        })
      );
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.hidePanel();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    }

    hidePanel() {
      if (!this.panel) return;
      this.panel.setAttribute('hidden', '');
      this.button.setAttribute('aria-expanded', 'false');
    }

    togglePanel() {
      const opening = this.panel.hasAttribute('hidden');
      this.panel.toggleAttribute('hidden', !opening);
      this.button.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) {
        const current = this.panel.querySelector('[aria-current="true"]');
        if (current) current.scrollIntoView({ block: 'nearest' });
      }
    }
  }
  defineElement('localization-form', LocalizationForm);

  /* -------------------------------------------------------------------------- */
  /* Show more button (facet lists etc.)                                        */
  /* -------------------------------------------------------------------------- */

  class ShowMoreButton extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const scope = this.closest('.parent-display') || this.parentElement;
        if (!scope) return;
        this.querySelectorAll('.label-text').forEach((label) => label.classList.toggle('hidden'));
        const items = scope.querySelectorAll('.show-more-item');
        items.forEach((item) => item.classList.toggle('hidden'));
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const firstRevealed = Array.from(items).find((item) => !item.classList.contains('hidden'));
        const focusable = firstRevealed ? firstRevealed.querySelector('input, a, button') : null;
        if (expanded && focusable) focusable.focus();
      });
    }
  }
  defineElement('show-more-button', ShowMoreButton);

  /* -------------------------------------------------------------------------- */
  /* Scroll-reveal animations                                                   */
  /* -------------------------------------------------------------------------- */

  const ThemeAnimations = {
    observer: null,

    get enabled() {
      return Boolean(window.themeConfig && window.themeConfig.animations);
    },

    get repeat() {
      return Boolean(window.themeConfig && window.themeConfig.repeatAnimations);
    },

    reveal(element) {
      element.classList.add('animate--shown', 'animate--visible');
    },

    conceal(element) {
      element.classList.remove('animate--shown', 'animate--visible');
    },

    createObserver() {
      const stickyBar = document.querySelector('.sticky-atc');
      const bottomOffset = 50 + (stickyBar ? stickyBar.clientHeight : 0);
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.reveal(entry.target);
              if (!this.repeat) this.observer.unobserve(entry.target);
            } else if (this.repeat) {
              this.conceal(entry.target);
            }
          });
        },
        { rootMargin: `0px 0px -${bottomOffset}px 0px` }
      );
    },

    init(root = document, instant = false) {
      const sections = root.querySelectorAll ? root.querySelectorAll('.animate-section') : [];
      const list = root.classList && root.classList.contains('animate-section') ? [root, ...sections] : Array.from(sections);
      if (!list.length) return;
      if (!this.enabled || instant || !('IntersectionObserver' in window)) {
        list.forEach((element) => this.reveal(element));
        return;
      }
      if (!this.observer) this.createObserver();
      list.forEach((element) => this.observer.observe(element));
    },
  };

  /* -------------------------------------------------------------------------- */
  /* Public API                                                                 */
  /* -------------------------------------------------------------------------- */

  Object.assign(window, {
    fetchConfig,
    debounce,
    throttle,
    trapFocus,
    removeTrapFocus,
    onKeyUpEscape,
    getFocusableElements,
    pauseAllMedia,
    formatMoney,
    subscribe,
    publish,
    PUB_SUB_EVENTS,
    ModalDialog,
    ModalOpener,
    DetailsModal,
    DetailsDisclosure,
    QuantityInput,
    DeferredMedia,
    InternalVideo,
    SliderComponent,
    CopyButton,
    LocalizationForm,
    ShowMoreButton,
    ThemeAnimations,
  });

  /* -------------------------------------------------------------------------- */
  /* Page-level behaviour                                                       */
  /* -------------------------------------------------------------------------- */

  function markExternalLinks(root = document) {
    root.querySelectorAll('a[href]').forEach((link) => {
      if (link.dataset.sameWindow === 'true' || link.target) return;
      if (link.hostname && link.hostname !== window.location.hostname && /^https?:$/.test(link.protocol)) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
    });
  }

  function bindSectionAtcButtons() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[id^="SectionAtcBtn-"]');
      if (!button) return;
      const mainForm = document.querySelector('product-form[data-main="true"]');
      if (mainForm && typeof mainForm.handleSubmit === 'function') mainForm.handleSubmit(event);
    });
  }

  function onReady(callback) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  }

  /* Content injected later (recommendations, quick add, section rendering) gets observed too. */
  function watchInjectedSections() {
    if (!('MutationObserver' in window)) return;
    const watcher = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches('.animate-section') || node.querySelector('.animate-section')) ThemeAnimations.init(node);
        });
      });
    });
    watcher.observe(document.body, { childList: true, subtree: true });
  }

  onReady(() => {
    ThemeAnimations.init(document);
    watchInjectedSections();
    markExternalLinks(document);
    bindSectionAtcButtons();
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      ThemeAnimations.init(event.target, true);
      markExternalLinks(event.target);
    });
    document.addEventListener('shopify:section:reorder', () => ThemeAnimations.init(document, true));
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const slider = block.closest('slider-component');
      if (slider && typeof slider.goToSlide === 'function') {
        slider.stopTimer();
        slider.pausedForEditor = true;
        const slide = block.closest('[id^="Slide-"], .slider__slide') || block;
        window.setTimeout(() => {
          slider.initPages();
          slider.goToSlide(slide, false);
        }, 120);
      }
      const section = block.closest('.animate-section');
      if (section) ThemeAnimations.reveal(section);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const slider = event.target.closest('slider-component');
      if (slider && slider.pausedForEditor) {
        slider.pausedForEditor = false;
        slider.restartTimer();
      }
    });
  }
})();
